/**
 * SongList — 播放器頁嘅**歌單**（分類 chips ＋ 逐行歌單）
 *
 * 血緣：`decisions/033`（分類入口）＋ `decisions/036`（清單 → 撳歌 → 播放器）
 * 由 `pages/PlayerPage.tsx` 拆出（`P-90`：頁面檔案硬規則 ≤200 行）
 *
 * ⚠️ 純顯示 ＋ 回呼（唔自己拉 API、唔自己決定播邊首）—— 資料同「撳一行之後點」由父層話事。
 * ⚠️ 讀唔到清單 → 父層負責**照實報**（呢個元件只顯示 `listError`）。
 */
import { useTranslation } from 'react-i18next';
import type { LibrarySong } from '../hooks/useLibrary';
import { playlistLabel } from '../playlists';
import type { Playlist } from '../hooks/usePlaylists';

/** 分類 chip（撳一下 ＝ 揀呢個清單） */
const CHIP = (on: boolean): React.CSSProperties => ({
  minHeight: '34px',
  padding: '0 12px',
  borderRadius: '17px',
  border: `1.5px solid ${on ? 'var(--color-primary)' : 'var(--color-divider)'}`,
  background: on ? 'var(--color-primary)' : 'var(--color-card)',
  color: on ? 'var(--color-on-primary)' : 'var(--color-text)',
  fontFamily: 'inherit',
  fontSize: 'var(--font-size-small, 15px)',
  fontWeight: 700,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
});

const ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  width: '100%',
  padding: '12px',
  borderBottom: '1px solid var(--color-divider)',
  background: 'transparent',
  color: 'var(--color-text)',
  fontFamily: 'inherit',
  textAlign: 'left',
  cursor: 'pointer',
};

const CIRCLE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '40px',
  minHeight: '40px',
  borderRadius: '999px',
  border: '1.5px solid var(--color-divider)',
  background: 'var(--color-card)',
  color: 'var(--color-text)',
  fontFamily: 'inherit',
  fontSize: '17px',
  cursor: 'pointer',
};

export interface SongListProps {
  /** 全部歌（〔全部歌〕chip 嘅數目；亦係空狀態判斷用） */
  songs: LibrarySong[];
  playlists: Playlist[];
  /** `null` ＝ 〔全部歌〕 */
  activeId: string | null;
  onPickActive: (id: string | null) => void;
  /** 撳一行（父層負責播 ＋ 入播放器） */
  onOpen: (song: LibrarySong) => void;
  /** 〔＋〕加入播放清單 */
  onAdd: (songId: string) => void;
  /** 而家播緊邊首（顯示「播放中」／「播放」） */
  currentId: string | null;
  playing: boolean;
  loading: boolean;
  /** 清單讀唔到（照實報） */
  listError: string | null;
}

export default function SongList({
  songs,
  playlists,
  activeId,
  onPickActive,
  onOpen,
  onAdd,
  currentId,
  playing,
  loading,
  listError,
}: SongListProps) {
  const { t } = useTranslation();
  return (
    <>
      {/* ⭐ 分類入口（產品負責人 2026-09-24：「player tap, insid will have catagory」） */}
      <div data-testid="player-categories" style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '10px' }}>
        <button
          type="button"
          data-testid="player-cat-all"
          aria-pressed={activeId === null}
          onClick={() => onPickActive(null)}
          style={CHIP(activeId === null)}
        >
          {t('playlist_all_songs')} · {t('playlist_count', { n: songs.length })}
        </button>
        {playlists.map((pl) => (
          <button
            key={pl.id}
            type="button"
            data-testid="player-cat"
            aria-pressed={activeId === pl.id}
            onClick={() => onPickActive(pl.id)}
            style={CHIP(activeId === pl.id)}
          >
            {playlistLabel(pl, t)} · {t('playlist_count', { n: pl.count })}
          </button>
        ))}
      </div>

      {loading ? <p className="page__loading">{t('chat_thinking')}</p> : null}
      {activeId && listError ? (
        <p className="page__error" role="alert">
          {t('playlist_load_failed')}（{listError}）
        </p>
      ) : null}
      {!loading && songs.length === 0 ? (
        <p className="footnote" style={{ textAlign: 'center' }} data-testid="player-empty-library">
          {activeId ? t('playlist_empty') : t('player_empty_body')}
        </p>
      ) : null}

      {/* ⭐ 歌單：撳一行 ＝ 入播放器（產品負責人：「then click into the song, you see the player」） */}
      <div data-testid="player-list" style={{ borderTop: '1px solid var(--color-divider)' }}>
        {(loading ? [] : songs).map((s) => (
          <div key={s.id} style={{ display: 'flex', alignItems: 'center' }}>
            <button type="button" data-testid={`player-row-${s.id}`} onClick={() => onOpen(s)} style={ROW}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontWeight: 700 }}>{s.title}</span>
                {s.journal ? (
                  <span
                    className="footnote"
                    style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {s.journal}
                  </span>
                ) : null}
              </span>
              <span className="footnote" style={{ whiteSpace: 'nowrap' }}>
                {currentId === s.id && playing ? t('player_now_playing') : t('player_play')}
              </span>
            </button>
            <button
              type="button"
              data-testid={`player-add-${s.id}`}
              aria-label={t('playlist_add_button')}
              onClick={() => onAdd(s.id)}
              style={{ ...CIRCLE, margin: '0 8px' }}
            >
              ＋
            </button>
          </div>
        ))}
      </div>
    </>
  );
}
